// Wrap format is mirrored in scripts/lib/skimlinks-rehype.mjs — keep in sync if the redirect shape changes.

export interface AffiliateUrlOptions {
  /** Skimlinks Site ID. Caller injects from its own environment (Vite env, Expo config, etc.). */
  siteId?: string;
  /** Optional custom tracking ID — appears as `xcust` in Skimlinks reports. Use product slugs or brand slugs for per-item attribution. */
  customId?: string;
}

/**
 * Wraps a merchant URL with Skimlinks Link Wrapper redirect for affiliate tracking.
 * Returns the original URL unchanged if `siteId` is not provided or the URL is empty.
 */
export function affiliateUrl(url: string, options: AffiliateUrlOptions = {}): string {
  const { siteId, customId } = options;
  if (!url || url === '#' || !siteId) return url;

  const params = new URLSearchParams({
    xs: '1',
    id: siteId,
    url,
  });
  if (customId) params.set('xcust', customId);
  return `https://go.redirectingat.com/?${params.toString()}`;
}

/** Checks whether a URL is already an affiliate-wrapped Skimlinks URL. */
export function isAffiliateUrl(url: string): boolean {
  return (
    url.startsWith('https://go.redirectingat.com/') ||
    url.startsWith('https://go.skimresources.com/')
  );
}

/**
 * GA4 drops or truncates an event parameter value over 100 characters, so
 * every string the `affiliate_click` event carries goes through this first.
 */
export const GA4_PARAM_MAX_LENGTH = 100;

/**
 * Takes `unknown`, not `string`: callers pass fields off API JSON, and the
 * products API sends no `slug` at all. A `.slice` on undefined threw inside
 * every card's click handler, so neither the GA4 event nor shop_click was
 * recorded (CAPSULE-CHIC-HUB-WEB-A). A missing value is an empty parameter.
 */
export function ga4Param(value: unknown): string {
  return typeof value === 'string' ? value.slice(0, GA4_PARAM_MAX_LENGTH) : '';
}

/** The host of a URL, lowercased, for analytics. Never the path: it can carry ids. */
export function merchantHostOf(url: string | null | undefined): string {
  if (!url) return 'unknown';
  try {
    return ga4Param(new URL(url).hostname.toLowerCase()) || 'unknown';
  } catch {
    return 'unknown';
  }
}
